import { IToast } from '@chakra-ui/react';
import {
  ArtIcon,
  BakingIcon,
  CookingIcon,
  DancingIcon,
  ExerciseIcon,
  GardeningIcon,
  HikingIcon,
  MusicIcon,
  OtherIcon,
  ReadingIcon,
  SingingIcon,
  SportsIcon,
  CovidIcon,
  LungsVirusIcon,
  PhysicalIcon,
  DriversLicenseIcon,
} from 'components/Icons';
import axios from 'axios';
import firebase from 'firebase';

export * from './validations';

export const formatPhoneNumber = (phoneNumber: string | undefined): string => {
  if (!phoneNumber) return 'N/A';
  const matches = phoneNumber.match(/(\+1)([0-9]{3})([0-9]{3})([0-9]{4})/);
  if (!matches || matches.length !== 5) {
    return phoneNumber;
  }
  const [, prefix, areaCode, exchange, number] = matches;
  return `${prefix} (${areaCode}) ${exchange}-${number}`;
};

export const capitalize = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

const getAvailableDays = (schedule: Schedule, join: string): string =>
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    .filter((day) => (schedule || ({} as IIndexable))[day])
    .join(join);

export const formatSchedule = (
  schedule: Schedule,
  type: ScheduleFormatType = 'DEFAULT',
): string => {
  const isDefault = type === 'DEFAULT';
  const join = isDefault ? ', ' : ' ';
  const availableDays = getAvailableDays(schedule, join);
  const append = isDefault ? ' ' : '\n';
  const base = `${availableDays}${append}`;
  const { liveIn, hours } = schedule;
  let text: string;
  switch (true) {
    case liveIn && !!hours:
      text = isDefault
        ? `up to ${hours} hours and for live-ins`
        : `\n${hours} hours and live-ins`;
      break;
    case liveIn:
      text = 'live-ins';
      break;
    case !!hours:
      text = `${isDefault ? 'up to ' : ''}${hours} hours${
        type !== 'HOME' ? ' per week' : ''
      }`;
      break;
    default:
      text = `${isDefault ? 'up to ' : ''}0 hours${
        type !== 'HOME' ? ' per week' : ''
      }`;
  }

  return base + text;
};

export const formatLegal = (legal: Legal): string => {
  const { allowedToWork, chargesOrWarrant } = legal;
  let workStatus: string;
  let chargesStatus;
  switch (allowedToWork) {
    case true:
      workStatus = 'Allowed to work';
      break;
    case false:
      workStatus = 'Not allowed to work';
      break;
    default:
      workStatus = '';
  }
  switch (chargesOrWarrant) {
    case true:
      chargesStatus = 'Criminal charges or warrants';
      break;
    case false:
      chargesStatus = 'No criminal charges or warrants';
      break;
    default:
      chargesStatus = '';
  }
  return `${workStatus}\n${chargesStatus}`;
};

export const selectPhoto = async ({
  inputId,
  showSpinner,
  hideSpinner,
  callback,
}: {
  inputId: string;
  showSpinner: () => void;
  hideSpinner: () => void;
  callback: (fileUrl: string, dataURL: any) => void;
}): Promise<void> => {
  const fileinput = document.getElementById(inputId) as HTMLInputElement;
  fileinput?.click();
  fileinput?.addEventListener('change', async () => {
    // access el.files[] to do something with it (test its length!)

    // add first image, if available
    if (fileinput?.files?.length) {
      //Show loader
      showSpinner();
      const selectedFile = fileinput?.files[0];
      const fileUrl = URL.createObjectURL(selectedFile);
      const dataURL = (await convertFile(selectedFile)) as any;
      callback(fileUrl, dataURL);
      hideSpinner();
    }
  });
};

export const convertFile = async (
  file: File,
): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    if (fileReader && file) {
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    } else {
      reject('No file provided');
    }
  });
};

export const getCheckButtonBg = (
  bool: boolean | string | number | undefined | null,
): string => (bool ? 'custom.levoBlack' : 'custom.levoWhite');

export const getCheckButtonColor = (
  bool: boolean | string | number | undefined | null,
): string => (bool ? 'custom.levoWhite' : 'custom.levoBlack');

export const fetchLocation = async (
  zipcode: string,
): Promise<{ status: any; city: string; state: string; message: any }> => {
  const clientKey = process.env.REACT_APP_ZIP_API_US;
  const url =
    'https://service.zipapi.us/zipcode/' + zipcode + '?X-API-KEY=' + clientKey;
  const response = await axios.get(url);
  const data = response.data;
  const {
    status,
    data: { city, state },
    message,
  } = data;
  return {
    status: status || '',
    city: city || '',
    state: state || '',
    message: message || '',
  };
};

export const aassociations = [
  { value: 'client', text: 'Client' },
  { value: 'scheduler', text: 'Scheduler' },
  { value: 'familyMember', text: "Client's Family Member" },
  { value: 'supervisingNurse', text: 'Supervising Nurse' },
  { value: 'poa', text: "Client's Power of Attorney" },
  { value: 'other', text: 'Other' },
  { value: 'businessOwner', text: 'Business Owner' },
];

export const aassociationsMap = {
  client: 'Client',
  scheduler: 'Scheduler',
  familyMember: "Client's Family Member",
  supervisingNurse: 'Supervising Nurse',
  poa: "Client's Power of Attorney",
  other: 'Other',
  businessOwner: 'Business Owner',
} as IIndexable;

export const careTypes = [
  { value: 'companion', text: 'Companion' },
  { value: 'conditionSpecific', text: 'Condition-specific' },
  { value: 'full', text: 'Full' },
];

export const careTypesMap = {
  companion: 'Companion',
  conditionSpecific: 'Condition-specific',
  full: 'Full',
} as IIndexable;

export const Ratings = [
  {
    title: 'Attendance',
    key: 'attendance',
    commentKey: 'attendanceComment',
    descriptions: {
      low: ['No call', 'No show'],
      medium: ['Occasionally', 'late'],
      high: ['Always', 'on time'],
    },
  },
  {
    title: 'Dependability',
    key: 'dependability',
    commentKey: 'dependabilityComment',
    descriptions: {
      low: ['A pain'],
      medium: ['What', "I'd expect"],
      high: ['Above &', 'beyond'],
    },
  },
  {
    title: 'Quality of work',
    key: 'qualityOfWork',
    commentKey: 'qualityOfWorkComment',
    descriptions: {
      low: ['Quality', 'issues'],
      medium: ['Did what', 'was asked'],
      high: ['Our top', 'pick'],
    },
  },
  {
    title: 'Job compentence',
    key: 'jobCompentence',
    commentKey: 'jobCompentenceComment',
    descriptions: {
      low: ['Woefully', 'unprepared'],
      medium: ['Generally', 'well-trained'],
      high: ['Highly', 'competent'],
    },
  },
] as Rating[];

// Keys must be incremental starting from 0 to preserve ordering
export const hobbiesArray = [
  { id: 0, key: 'Art', icon: ArtIcon },
  { id: 1, key: 'Baking', icon: BakingIcon },
  { id: 2, key: 'Cooking', icon: CookingIcon },
  { id: 3, key: 'Dancing', icon: DancingIcon },
  { id: 4, key: 'Exercise', icon: ExerciseIcon },
  { id: 5, key: 'Gardening', icon: GardeningIcon },
  { id: 6, key: 'Hiking', icon: HikingIcon },
  { id: 7, key: 'Music', icon: MusicIcon },
  { id: 8, key: 'Reading', icon: ReadingIcon },
  { id: 9, key: 'Singing', icon: SingingIcon },
  { id: 10, key: 'Sports', icon: SportsIcon },
  { id: 11, key: 'Other', icon: OtherIcon },
];

export const savePrompt =
  "You haven't saved your profile yet. Are you sure you want to leave and discard your changes?";

export const OnboardingDefaults = {
  firstname: '',
  lastname: '',
  address: {
    city: '',
    state: '',
    streetAddress: '',
    zipCode: '',
  },
  schedule: {
    Mon: false,
    Tue: false,
    Wed: false,
    Thu: false,
    Fri: false,
    Sat: false,
    Sun: false,
    hourly: false,
    liveIn: false,
  },
  hobbies: [],
  photoURL: '',
  email: '',
  password: '',
  confirmPassword: '',
  phoneNumber: '',
  legal: {
    allowedToWork: null,
    chargesOrWarrant: null,
    explanation: '',
  },
  notifications: {
    allNotifications: false,
    textNotifications: true,
    emailNotifications: true,
    questionNotifications: true,
    jobNotifications: true,
  },
  notificationsFrequency: 'Real Time',
};

export const SUCCESS_TOAST: IToast = {
  duration: 3000,
  title: 'Success.',
  status: 'success',
  isClosable: true,
};

export const ERROR_TOAST: IToast = {
  duration: 3000,
  title: 'Error.',
  status: 'error',
  isClosable: true,
};

export const regularRouteorder = {
  names: 1,
  zipCode: 2,
  experienceAndCertification: 3,
  workPreferences: 4,
  driversLicense: 5,
  car: 6,
  legal: 7,
  hobbies: 8,
  profilePicture: 9,
  signup: 10,
  phoneNumber: 10,
} as IIndexable;

export const refDetailsDefaults: ReferenceDetails = {
  userId: '',
  email: '',
  association: '',
  city: '',
  state: '',
  firstname: '',
  lastname: '',
  clientFirstname: '',
  clientLastname: '',
  startDate: new Date(),
  endDate: new Date(),
  company: '',
  careType: '',
};

export const medicalDocuments = [
  { tag: 'COVID', key: 'COVID', icon: CovidIcon },
  { tag: 'TB Test', key: 'Tb', icon: LungsVirusIcon },
  { tag: 'Physical', key: 'Physical', icon: PhysicalIcon },
];

export const legalDocuments = [
  {
    tag: "Driver's license",
    key: 'Driver’s \nLicense/ID',
    icon: DriversLicenseIcon,
  },
];

export const certificationsRegular = [
  { key: 'hha', value: 'HHA' },
  { key: 'cna', value: 'CNA' },
  { key: 'cprAndFirstAid', value: 'CPR & First Aid' },
  { key: 'qmap', value: 'QMAP' },
];

export const jobStatuses = [
  {
    key: 1,
    title: 'Assigned agency',
    description: 'Exciting stuff! You’ve been assigned to an agency',
    tasks: [
      {
        key: 1.1,
        title: 'Fill out application questions',
        description: 'Tap/click to go to application questions',
        action: 'applicationQuestions',
      },
      {
        key: 1.2,
        title: 'Request 3 references',
        description: 'Tap/click to go to references page',
        url: '/references',
      },
      {
        key: 1.3,
        title: 'Upload documents',
        description: 'Tap/click to go to documents page',
        url: '/documents',
      },
    ],
  },
  {
    key: 2,
    title: 'Sent to agency',
    description:
      "You've successfully been sent to an agency. Hang tight, agency is deciding if they want to interview.",
  },
  {
    key: 3,
    title: 'Interviewing',
    description: 'Waiting for agency to make hiring decision',
    tasks: [
      {
        key: 3.1,
        title: 'Set up interview',
        description: 'Tap/click to choose interview date & time',
        url: '/interview',
      },
      {
        key: 3.2,
        title: 'Attend interview',
        description: 'Tap/click to view interview date & time',
        url: '/interview',
      },
    ],
  },
  {
    key: 4,
    title: 'Employer onboarding',
    description:
      'Look out for an email with onboarding information from your assigned agency.',
    tasks: [
      {
        key: 4.1,
        title: 'Upload onboarding documents',
        description: 'Tap/click to go to documents page',
        url: '/documents',
      },
    ],
  },
  {
    key: 5,
    title: 'Started',
    description:
      'Congrats! You’re all set to go on the job. Have a great first day.',
  },
];

export const isAdmin = (user?: firebase.User): boolean => {
  const re = new RegExp(process.env.REACT_APP_ADMIN_USER || '', 'g');
  return re.test(user?.email || '');
};

export const skillsArray = [
  { id: 0, key: 'dementia', value: 'Dementia' },
  { id: 1, key: 'incontinence', value: 'Incontinence' },
  { id: 2, key: 'hospice', value: 'Hospice' },
  { id: 3, key: 'hoyerLift', value: 'Hoyer lift' },
  { id: 4, key: 'parkinsons', value: 'Parkinsons' },
  { id: 5, key: 'gaitBelt', value: 'Gait belt' },
  { id: 6, key: 'stroke', value: 'Stroke' },
];

export const okayWithArray = [
  { id: 0, key: 'cats', value: 'Cats?' },
  { id: 1, key: 'dogs', value: 'Dogs?' },
  { id: 2, key: 'smokingClients', value: 'Clients who smoke?' },
  { id: 3, key: 'maleClients', value: 'Male clients?' },
  { id: 4, key: 'femaleClients', value: 'Female clients' },
];

export const languageOptions = [
  { id: 0, label: 'English', value: 'English' },
  { id: 1, label: 'Spanish', value: 'Spanish' },
  { id: 2, label: 'French', value: 'French' },
  { id: 3, label: 'German', value: 'German' },
  { id: 4, label: 'Italian', value: 'Italian' },
  { id: 5, label: 'Portuguese', value: 'Portuguese' },
  { id: 6, label: 'Russian', value: 'Russian' },
  { id: 7, label: 'Chinese', value: 'Chinese' },
  { id: 8, label: 'Japanese', value: 'Japanese' },
  { id: 9, label: 'Korean', value: 'Korean' },
  { id: 10, label: 'Arabic', value: 'Arabic' },
  { id: 11, label: 'Armenian', value: 'Armenian' },
  { id: 12, label: 'French Creole', value: 'French Creole' },
  { id: 13, label: 'Hebrew', value: 'Hebrew' },
  { id: 14, label: 'Hindi', value: 'Hindi' },
  { id: 15, label: 'Greek', value: 'Greek' },
  { id: 16, label: 'Gujarati', value: 'Gujarati' },
  { id: 17, label: 'Persian', value: 'Persian' },
  { id: 18, label: 'Polish', value: 'Polish' },
  { id: 19, label: 'Tagalog', value: 'Tagalog' },
  { id: 20, label: 'Urdu', value: 'Urdu' },
  { id: 21, label: 'Vietnamese', value: 'Vietnamese' },
  { id: 22, label: 'Other', value: 'Other' },
];

export const educationLevelOptions = [
  { id: 0, key: 'highSchoolDiploma', value: 'High school diploma' },
  { id: 1, key: 'collegeDegree', value: 'College degree' },
  { id: 2, key: 'other', value: 'Other' },
  { id: 3, key: 'noneOfTheAbove', value: 'None of the above' },
];

export const applicationQuestionDefaults = {
  skills: {
    dementia: false,
    inontinence: false,
    hospice: false,
    hoyerLift: false,
    parkinsons: false,
    gaitBelt: false,
    stroke: false,
  },
  okayWith: {
    cats: false,
    dogs: false,
    smokingClients: false,
    maleClients: false,
    femaleClients: false,
  },
  languages: [] as string[],
  largestWeight: 0,
  educationLevel: {
    highSchoolDiploma: false,
    collegeDegree: false,
    other: false,
    noneOfTheAbove: false,
  },
};
